import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import ImageWithBasePath from '../../../core/common/imageWithBasePath';
import {
  MANUAL_CATEGORIES,
  S3_MANUAL_ITEMS,
  copyToClipboard,
  downloadFileFromS3,
} from './manualsData';
import { ManualItem, ManualFilterState } from './types';
import './UserManual.scss';

const UserManual: React.FC = () => {
  const [manualItems] = useState<ManualItem[]>(S3_MANUAL_ITEMS);
  const [activeItem, setActiveItem] = useState<ManualItem | null>(null);
  const [mediaModalType, setMediaModalType] = useState<'video' | 'pdf' | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Filter State
  const [filters, setFilters] = useState<ManualFilterState>({
    search: '',
    folderType: 'all',
    category: 'all',
    sortBy: 'number',
  });

  // Shortcut '/' to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter and Sort Items
  const filteredItems = useMemo(() => {
    return manualItems
      .filter((item) => {
        // Folder Type Filter
        if (filters.folderType !== 'all' && item.folderType !== filters.folderType) {
          return false;
        }
        // Module Category Filter
        if (filters.category !== 'all' && item.category !== filters.category) {
          return false;
        }
        // Search Filter
        if (filters.search.trim()) {
          const query = filters.search.toLowerCase().trim();
          const matchesTitle = item.title.toLowerCase().includes(query);
          const matchesFileName = item.fileName.toLowerCase().includes(query);
          const matchesDesc = item.description?.toLowerCase().includes(query) || false;
          const matchesCat = item.categoryLabel.toLowerCase().includes(query);
          const matchesFolder = item.parentFolder.toLowerCase().includes(query);
          if (!matchesTitle && !matchesFileName && !matchesDesc && !matchesCat && !matchesFolder) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        if (filters.sortBy === 'title') {
          return a.title.localeCompare(b.title);
        }
        if (filters.sortBy === 'version') {
          return (b.version || '').localeCompare(a.version || '');
        }
        // Default sort: natural numeric file order
        return a.fileName.localeCompare(b.fileName, undefined, { numeric: true });
      });
  }, [manualItems, filters]);

  // Counts by folder type
  const folderCounts = useMemo(() => {
    const empManuals = manualItems.filter((i) => i.folderType === 'employee_manual').length;
    const coManuals = manualItems.filter((i) => i.folderType === 'co_manual').length;
    const videos = manualItems.filter((i) => i.folderType === 'employee_video').length;
    return {
      all: manualItems.length,
      employee_manual: empManuals,
      co_manual: coManuals,
      employee_video: videos,
    };
  }, [manualItems]);

  // Handle Copy S3 Link
  const handleCopyS3Link = async (item: ManualItem) => {
    const success = await copyToClipboard(item.s3Url);
    if (success) {
      setToastMessage(`Copied S3 link for ${item.title.substring(0, 30)}...`);
      setTimeout(() => setToastMessage(null), 2500);
    }
  };

  // Open Media Modal
  const handleOpenMedia = (item: ManualItem) => {
    setActiveItem(item);
    setMediaModalType(item.type === 'video' ? 'video' : 'pdf');
  };

  // Close Media Modal
  const handleCloseModal = () => {
    setActiveItem(null);
    setMediaModalType(null);
  };

  return (
    <div className="dmrc-manual-portal">
      {/* Top Navbar */}
      <nav className="portal-navbar">
        <div className="container-fluid px-lg-4 px-3">
          <div className="portal-navbar-inner">
            <Link to={all_routes.login} className="brand-container">
              <ImageWithBasePath
                src="assets/img/dmrc-metro-logo.svg"
                alt="Delhi Metro Logo"
                className="brand-logo-img"
                height={36}
                width={36}
              />
              <div className="brand-text-block">
                <div className="brand-title-row">
                  <span className="brand-title">DMRC HRMS</span>
                  <span className="brand-badge d-none d-sm-inline-flex">Knowledge Portal</span>
                </div>
                <span className="brand-subtitle d-none d-md-inline">
                  Delhi Metro Rail Corporation
                </span>
              </div>
            </Link>

            <div className="nav-action-group">
              <Link
                to={all_routes.introVideo}
                className="btn-nav-video"
                title="Watch Introduction Video"
              >
                <i className="ti ti-player-play" />
                <span className="d-none d-md-inline">Watch Intro Video</span>
                <span className="d-none d-sm-inline d-md-none">Intro Video</span>
                <span className="d-sm-none">Video</span>
              </Link>

              <Link to={all_routes.login} className="btn-login-header">
                <i className="ti ti-login" />
                <span className="d-none d-sm-inline">Employee Login</span>
                <span className="d-sm-none">Login</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Body */}
      <div className="portal-main-body">
        <div className="container-fluid px-lg-4 px-3">
          {/* Refined Page Header Block */}
          <div className="portal-page-header">
            <div className="d-md-flex align-items-center justify-content-between">
              <div>
                <ul className="breadcrumb-list">
                  <li>
                    <Link to={all_routes.login}>Home</Link>
                  </li>
                  <li className="crumb-sep">/</li>
                  <li>
                    <span>Employee Services</span>
                  </li>
                  <li className="crumb-sep">/</li>
                  <li className="active-crumb">User Manuals & Video Guides</li>
                </ul>

                <h3 className="header-title-text">User Manuals & Video Tutorials</h3>
                <p className="header-subtitle-text">
                  Official DMRC HRMS standard operating procedures, CO approval manuals, and video walkthroughs.
                </p>
              </div>

              <div className="header-stat-chips mt-md-0 mt-3">
                <div className="stat-chip">
                  <i className="ti ti-files text-primary" />
                  <span>
                    Total: <strong>{folderCounts.all}</strong>
                  </span>
                </div>
                <div className="stat-chip">
                  <i className="ti ti-file-type-pdf text-danger" />
                  <span>
                    PDFs: <strong>{folderCounts.employee_manual + folderCounts.co_manual}</strong>
                  </span>
                </div>
                <div className="stat-chip">
                  <i className="ti ti-video text-success" />
                  <span>
                    Videos: <strong>{folderCounts.employee_video}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Clean Filter & Folder Tabs Card */}
          <div className="portal-filter-card">
            {/* Folder Tabs */}
            <div className="folder-nav-tabs">
              <button
                type="button"
                className={`nav-tab-btn ${filters.folderType === 'all' ? 'active' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, folderType: 'all' }))}
              >
                <i className="ti ti-layout-grid" />
                <span>All Resources</span>
                <span className="tab-count-badge">{folderCounts.all}</span>
              </button>

              <button
                type="button"
                className={`nav-tab-btn ${filters.folderType === 'employee_manual' ? 'active' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, folderType: 'employee_manual' }))}
              >
                <i className="ti ti-file-text text-danger" />
                <span>Employee Manuals</span>
                <span className="tab-count-badge">{folderCounts.employee_manual}</span>
              </button>

              <button
                type="button"
                className={`nav-tab-btn ${filters.folderType === 'co_manual' ? 'active' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, folderType: 'co_manual' }))}
              >
                <i className="ti ti-checkup-list text-primary" />
                <span>CO Approvals</span>
                <span className="tab-count-badge">{folderCounts.co_manual}</span>
              </button>

              <button
                type="button"
                className={`nav-tab-btn ${filters.folderType === 'employee_video' ? 'active' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, folderType: 'employee_video' }))}
              >
                <i className="ti ti-video text-success" />
                <span>Video Tutorials</span>
                <span className="tab-count-badge">{folderCounts.employee_video}</span>
              </button>
            </div>

            {/* Search and Category Filter Inputs */}
            <div className="filter-inputs-row">
              <div className="search-field-wrap">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="form-control"
                    placeholder="Search by keyword, module, e.g. Leave, FRAS, VPF, Tax..."
                    value={filters.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  />
                  {filters.search && (
                    <button
                      type="button"
                      className="btn-clear"
                      onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}
                      title="Clear search"
                    >
                      <i className="ti ti-x" />
                    </button>
                  )}
                </div>
              </div>

              <div className="category-select-wrap">
                <select
                  className="form-select"
                  value={filters.category}
                  onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
                >
                  {MANUAL_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sort-select-wrap">
                <select
                  className="form-select"
                  value={filters.sortBy}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      sortBy: e.target.value as ManualFilterState['sortBy'],
                    }))
                  }
                >
                  <option value="number">Sort: File Order</option>
                  <option value="title">Sort: Title (A-Z)</option>
                  <option value="version">Sort: Version</option>
                </select>
              </div>
            </div>
          </div>

          {/* Cards Grid */}
          {filteredItems.length > 0 ? (
            <div className="cards-grid-wrapper">
              {filteredItems.map((item) => {
                const isVideo = item.type === 'video';
                const isCo = item.folderType === 'co_manual';

                return (
                  <div key={item.id} className="portal-resource-card">
                    {/* Header: Video Thumbnail or PDF Header */}
                    {isVideo ? (
                      <div
                        className="video-preview-header"
                        onClick={() => handleOpenMedia(item)}
                        title="Click to stream video"
                      >
                        <img
                          src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80"
                          alt={item.title}
                        />
                        <div className="video-overlay-tint">
                          <div className="play-btn-round">
                            <i className="ti ti-player-play ps-1" />
                          </div>
                        </div>
                        <span className="video-type-tag">
                          <i className="ti ti-video" /> Video Guide
                        </span>
                        <span className="video-duration-tag">
                          <i className="ti ti-clock me-1" /> MP4 HD
                        </span>
                      </div>
                    ) : (
                      <div className="pdf-preview-header">
                        <div className="pdf-icon-avatar">
                          <i className="ti ti-file-type-pdf" />
                        </div>
                        <div className="pdf-badge-group">
                          {item.version && <span className="version-chip">{item.version}</span>}
                          {isCo ? (
                            <span className="folder-chip co">CO Approval</span>
                          ) : (
                            <span className="folder-chip emp">Employee SOP</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Body Content */}
                    <div className="card-body-section">
                      <div className="card-category-lbl">{item.categoryLabel}</div>
                      <h4 className="card-title-text" title={item.fileName}>
                        {item.title}
                      </h4>
                      <p className="card-desc-text">{item.description}</p>

                      {/* Card Actions */}
                      <div className="card-actions-bar">
                        <button
                          type="button"
                          className="btn-card-primary"
                          onClick={() => handleOpenMedia(item)}
                        >
                          {isVideo ? (
                            <>
                              <i className="ti ti-player-play" /> Play Video
                            </>
                          ) : (
                            <>
                              <i className="ti ti-book" /> Read PDF
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn-card-download"
                          onClick={() => downloadFileFromS3(item.s3Url, item.fileName)}
                          title="Download from S3"
                        >
                          <i className="ti ti-download" /> Download
                        </button>

                        <button
                          type="button"
                          className="btn-card-copy"
                          onClick={() => handleCopyS3Link(item)}
                          title="Copy Direct S3 URL"
                        >
                          <i className="ti ti-link" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-results-box">
              <div className="empty-icon-round">
                <i className="ti ti-folder-off" />
              </div>
              <h5>No matching resources found</h5>
              <p>Try searching with another keyword or reset your active filters.</p>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setFilters({
                    search: '',
                    folderType: 'all',
                    category: 'all',
                    sortBy: 'number',
                  })
                }
              >
                Reset All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="portal-footer-bottom text-center">
        <div className="container-fluid px-lg-4 px-3">
          <div className="footer-row">
            <div>
              &copy; {new Date().getFullYear()} Delhi Metro Rail Corporation (DMRC). All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* Media Viewer Modal (Video & PDF) */}
      {activeItem && mediaModalType && (
        <div className="portal-media-modal-backdrop" onClick={handleCloseModal}>
          <div
            className="media-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="modal-header-bar">
              <div className="modal-title-text" title={activeItem.fileName}>
                {activeItem.title}
              </div>

              <div className="modal-header-actions">
                <button
                  type="button"
                  className="btn-modal-action"
                  onClick={() => downloadFileFromS3(activeItem.s3Url, activeItem.fileName)}
                  title="Download File"
                >
                  <i className="ti ti-download" />
                  <span className="d-none d-sm-inline ms-1">Download</span>
                </button>

                <a
                  href={activeItem.s3Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-modal-action text-white"
                  title="Open in New Tab"
                >
                  <i className="ti ti-external-link" />
                  <span className="d-none d-sm-inline ms-1">Open</span>
                </a>

                <button
                  type="button"
                  className="btn-modal-action btn-close-modal"
                  onClick={handleCloseModal}
                  title="Close Viewer"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-media-body">
              {mediaModalType === 'video' ? (
                <div className="video-player-container">
                  <video
                    controls
                    autoPlay
                    playsInline
                    src={activeItem.s3Url}
                  >
                    <source src={activeItem.s3Url} type="video/mp4" />
                    Your browser does not support HTML5 video streaming.
                  </video>
                </div>
              ) : (
                <div className="pdf-player-container">
                  <object
                    data={`${activeItem.s3Url}#toolbar=1&navpanes=1`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                  >
                    <iframe
                      src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                        activeItem.s3Url
                      )}&embedded=true`}
                      title={activeItem.title}
                      width="100%"
                      height="100%"
                    >
                      <div className="p-4 text-center">
                        <p>PDF preview is ready.</p>
                        <a
                          href={activeItem.s3Url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          Open & Download from S3
                        </a>
                      </div>
                    </iframe>
                  </object>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="toast-copy-notification">
          <i className="ti ti-check text-success" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};

export default UserManual;
